import { RouteObject } from "react-router";
import { BailleurItem, IApiService, LogItem, UserDetail } from "./services/ApiService";
import { LoginPage } from "./pages/account/LoginPage";
import { MainLayout, searchContext } from "./layout/MainLayout";
import { logoutAction } from "./pages/account/LogoutPage.action";
import { LogoutPage } from "./pages/account/LogoutPage";
import { ErrorPage } from "./pages/utility/ErrorPage";
import { Box, InputBase } from "@mui/material";
import { BailleursPage } from "./pages/bailleurs/BailleursPage";
import { bailleursPageLoader } from "./pages/bailleurs/BailleursPage.loader";
import { ProgrammesPage } from "./pages/bailleur/programmes/ProgrammesPage";
import { programmesPageLoader } from "./pages/bailleur/programmes/ProgrammesPage.loader";
import { bailleurPageLoader } from "./pages/bailleur/BailleurPage.loader";
import { LogementsPage } from "./pages/bailleur/logements/LogementsPage";
import { loglementsPageLoader } from "./pages/bailleur/logements/LogementsPage.loader";
import { ViewKind } from "./pages/logement/LogementPageBase";
import { NewLogementPage } from "./pages/logement/new/NewLogementPage";
import { newLogementPageLoader } from "./pages/logement/new/NewLogementPage.loader";
import { ViewLogementPage } from "./pages/logement/view/ViewLogementPage";
import { viewLogementPageLoader } from "./pages/logement/view/ViewLogementPage.loader";
import { EditLogementPage } from "./pages/logement/edit/EditLogementPage";
import { editLogementPageLoader } from "./pages/logement/edit/EditLogementPage.loader";
import { Logement } from "./dataModel/generated/Logement";
import { NewTabs } from "./pages/logement/new/tabs/NewTabs";
import { SaveTabContent as NewSaveTabContent } from "./pages/logement/new/tabs/SaveTabContent";
import { ViewTabs } from "./pages/logement/view/tabs/ViewTabs";
import { HistoriqueTabContent } from "./pages/logement/view/tabs/HistoriqueTabContent";
import { EditTabs } from "./pages/logement/edit/tabs/EditTabs";
import { SaveTabContent as EditSaveTabContent } from "./pages/logement/edit/tabs/SaveTabContent";
import { LogementTabContent } from "./pages/logement/shared-tabs/LogementTabContent";
import { DiagnosticTabContent } from "./pages/logement/shared-tabs/DiagnosticTabContent";
import { DocumentsTabContent } from "./pages/logement/shared-tabs/DocumentsTabContent";
import { CommentsTabContent } from "./pages/logement/shared-tabs/CommentsTabContent";
import { ForgotPasswordPage } from "./pages/account/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/account/ResetPasswordPage";
import { MePage } from "./pages/account/MePage";
import { mePageLoader } from "./pages/account/MePage.loader";
import { useContext } from "react";
import { HomePage } from "./pages/root/HomePage";
import { homePageLoader } from "./pages/root/HomePage.loader";
import { mainLayoutLoader } from "./layout/MainLayout.loader";
import {
    mainCrumb,
    loginCrumb,
    forgotPasswordCrumb,
    resetPasswordCrumb,
    meCrumb,
    bailleursPageCrumb,
    bailleurCrumb,
    logementsCrumb,
    newLogementCrumb,
    viewLogementCrumb,
    editLogementCrumb
} from "./routeFactory.breadcrumbs";

export const routeFactory = (apiService: IApiService): RouteObject[] => {
    // Routes publiques et protégées unifiées sous MainLayout
    const mainRoute: RouteObject = {
        path: "/",
        errorElement: <ErrorPage />,
        element: <MainLayout apiService={apiService}/>,
        loader: mainLayoutLoader(apiService),
        handle: {
            crumb: mainCrumb
        },
        children: [
            // Routes publiques (account)
            {
                path: "account/login",
                element: <LoginPage api={apiService} />,
                handle: {
                    crumb: loginCrumb
                }
            },
            {
                path: "account/forgot-password",
                element: <ForgotPasswordPage api={apiService} />,
                handle: {
                    crumb: forgotPasswordCrumb
                }
            },
            {
                path: "account/reset-password",
                element: <ResetPasswordPage api={apiService} />,
                handle: {
                    crumb: resetPasswordCrumb
                }
            },
            {
                path: "account/logout",
                element: <LogoutPage />,
                action: logoutAction(apiService)
            },
            {
                path: "account/me",
                element: <MePage apiService={apiService}/>,
                loader: mePageLoader(apiService),
                handle: {
                    crumb: meCrumb
                }
            },
            {
                index: true,
                element: <HomePage/>,
                loader: homePageLoader(apiService),
            },
            {
                path: "/bailleurs",
                element: <BailleursPage />,
                loader: bailleursPageLoader(apiService),
                handle: {
                    crumb: bailleursPageCrumb(apiService)
                },
            },
            {
                path: "/:bailleurId",
                loader: bailleurPageLoader(apiService),
                handle: {
                    crumb: bailleurCrumb(apiService)
                },
                children: [{
                index: true,
                element: <ProgrammesPage />,
                loader: programmesPageLoader(apiService),
                handle: {
                    subHeader: (_path: string): React.FunctionComponent => {
                        return () => <SearchBar />;
                    },
                }
            }, {
                path:"logements/:programme?",
                element: <LogementsPage />,
                loader: loglementsPageLoader(apiService),
                handle: {
                    crumb: logementsCrumb
                }
            }]
        }, {
            path:"new",
            element: <NewLogementPage />,
            loader: newLogementPageLoader(apiService),
            handle: {
                subHeader: (path: string): React.FunctionComponent => {
                    return () => <NewTabs path={path}/>
                },
                crumb: newLogementCrumb(apiService)
            },
            children: [{
                index: true,
                element: <LogementTabContent viewKind={ViewKind.New} />
            }, {
                path: "diagnostic",
                element: <DiagnosticTabContent viewKind={ViewKind.New} />
            }, {
                path: "comments",
                element: <CommentsTabContent viewKind={ViewKind.New} />
            }, {
                path: "documents",
                element: <DocumentsTabContent viewKind={ViewKind.New} />
            }, {
                path: "save",
                element: <NewSaveTabContent viewKind={ViewKind.New} />
            }]
        }, {
            path:"logement/:logementId/view",
            element: <ViewLogementPage />,
            loader: viewLogementPageLoader(apiService),
            handle: {
                subHeader: (path: string): React.FunctionComponent => {
                    return () => <ViewTabs path={path}/>
                },
                crumb: viewLogementCrumb(apiService)
            },
            children: [{
                index: true,
                element: <LogementTabContent viewKind={ViewKind.Read} />
            }, {
                path: "diagnostic",
                element: <DiagnosticTabContent viewKind={ViewKind.Read} />
            }, {
                path: "comments",
                element: <CommentsTabContent viewKind={ViewKind.Read} />
            }, {
                path: "documents",
                element: <DocumentsTabContent viewKind={ViewKind.Read} />
            }, {
                path: "historique",
                element: <HistoriqueTabContent apiService={apiService}/>
            }]
        }, {
            path:"logement/:logementId/edit",
            element: <EditLogementPage />,
            loader: editLogementPageLoader(apiService),
            handle: {
                subHeader: (path: string): React.FunctionComponent => {
                    return () => <EditTabs path={path} />
                },
                crumb: editLogementCrumb(apiService)
            },
            children: [{
                index: true,
                element: <LogementTabContent viewKind={ViewKind.Modify} />
            }, {
                path: "diagnostic",
                element: <DiagnosticTabContent viewKind={ViewKind.Modify} />
            }, {
                path: "comments",
                element: <CommentsTabContent viewKind={ViewKind.Modify} />
            }, {
                path: "documents",
                element: <DocumentsTabContent viewKind={ViewKind.Modify} />
            }, {
                path: "save",
                element: <EditSaveTabContent viewKind={ViewKind.Modify} />
            }]
        }]
    };

    const routes: RouteObject[] = [mainRoute];

    return routes;
}

// Specific route data types for better type safety
export interface PublicRouteData {
    // No specific data for public routes (login, forgot-password, reset-password)
}

export interface HomePageData {
    readonly bailleurId: number;
}

export interface BailleurPageData {
    readonly bailleur: BailleurItem | null; // null = "hors bailleur"
}

export interface ProgrammesPageData extends BailleurPageData {
    // Inherits bailleur, no additional data needed
}

export interface BailleursPageData {
    readonly bailleurs: ReadonlyArray<BailleurItem>;
}

export interface LogementsPageData extends BailleurPageData {
    readonly programme: string | null;
    readonly logements: ReadonlyArray<Logement>;
}

export interface LogementPageData extends BailleurPageData {
    readonly programme: string | null;
    readonly logement: Logement;
    readonly bailleurs: ReadonlyArray<BailleurItem>;
    readonly logEntries: ReadonlyArray<LogItem>;
    readonly communes: ReadonlyArray<{ readonly codeINSEE: string; readonly codePostal: string; readonly nom: string }>;
    readonly forceProgramme?: boolean;
    readonly forceBailleur?: boolean;
}

export interface MePageData {
    readonly userDetail: UserDetail;
}

// Union type for all possible route data - kept for React Router compatibility
export type MainRouteData =
    | PublicRouteData
    | HomePageData
    | BailleurPageData
    | ProgrammesPageData
    | BailleursPageData
    | LogementsPageData
    | LogementPageData
    | MePageData;

const SearchBar = () => {
    const context = useContext(searchContext);
    return <Box>
        {context && <InputBase
            value={context.search}
            onChange={(ev) => context.setSearch(ev.target.value)}
            placeholder="Rechercher…"
            inputProps={{ 'aria-label': 'rechercher' }}
        />}
    </Box>;
}